import React, { useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CloudUpload, Create, ExpandMore } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import WizardPageShell from "./WizardPageShell";

const POLL_MS = 3000;
const MAX_POLLS = 60;

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function WizardWritingCoach() {
  const inputRef = useRef(null);
  const [mode, setMode] = useState("upload"); // upload | type
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("capture");
  const [statusMessage, setStatusMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [error, setError] = useState("");

  const reset = () => {
    setFiles([]);
    setText("");
    setPhase("capture");
    setStatusMessage("");
    setSummary(null);
    setPieces([]);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const startReview = async () => {
    setError("");
    setPhase("processing");
    setStatusMessage("Sending your writing…");
    try {
      let sessionId = "";
      if (mode === "type") {
        const trimmed = text.trim();
        if (trimmed.length < 30) {
          throw new Error("Please write at least 30 characters.");
        }
        const res = await api.post("student/wizard/writing-coach/review", { text: trimmed });
        sessionId = String(res.data?.data?.session_id || "");
      } else {
        if (files.length === 0) throw new Error("Upload at least one image or PDF.");
        const form = new FormData();
        files.forEach((f) => form.append("files[]", f));
        const res = await api.post("student/wizard/writing-coach/review", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        sessionId = String(res.data?.data?.session_id || "");
      }
      if (!sessionId) throw new Error("Missing session.");
      setStatusMessage("AI is reviewing your writing…");
      for (let i = 0; i < MAX_POLLS; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
        // eslint-disable-next-line no-await-in-loop
        const statusRes = await api.get("student/wizard/writing-coach/status", {
          params: { session_id: sessionId },
        });
        const data = statusRes.data?.data || {};
        const status = String(data.status || "");
        if (data.message) setStatusMessage(String(data.message));
        if (status === "completed") {
          setSummary(data.summary || null);
          setPieces(Array.isArray(data.pieces) ? data.pieces : []);
          setPhase("results");
          return;
        }
        if (status === "failed") {
          throw new Error(data.message || "Review failed.");
        }
      }
      throw new Error("Review is taking too long. Please try again.");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Could not review your writing.";
      setError(msg);
      setPhase("capture");
      toast.error(msg);
    }
  };

  const pct = Number(summary?.overall_percentage);
  const hasPct = Number.isFinite(pct);

  return (
    <WizardPageShell
      title="Writing Coach"
      subtitle="Upload or type creative writing for expert feedback"
    >
      {phase === "capture" ? (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Button
              variant={mode === "upload" ? "contained" : "outlined"}
              startIcon={<CloudUpload />}
              onClick={() => setMode("upload")}
              sx={{ textTransform: "none" }}
            >
              Upload
            </Button>
            <Button
              variant={mode === "type" ? "contained" : "outlined"}
              startIcon={<Create />}
              onClick={() => setMode("type")}
              sx={{ textTransform: "none" }}
            >
              Type paragraph
            </Button>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {mode === "upload" ? (
            <>
              <Typography color="text.secondary">
                Upload photos or a PDF of your writing (up to 8 files).
              </Typography>
              <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                hidden
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => inputRef.current?.click()}
                sx={{ textTransform: "none", alignSelf: "flex-start" }}
              >
                Choose files
              </Button>
              {files.map((f) => (
                <Typography key={f.name + f.size} variant="body2" color="text.secondary">
                  {f.name}
                </Typography>
              ))}
            </>
          ) : (
            <TextField
              multiline
              minRows={6}
              fullWidth
              label="Your writing"
              helperText={`${text.trim().length}/8000 (min 30)`}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 8000))}
            />
          )}

          <Button
            variant="contained"
            onClick={startReview}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              alignSelf: "flex-start",
              bgcolor: "#047857",
              "&:hover": { bgcolor: "#065F46" },
            }}
          >
            Review my writing
          </Button>
        </Stack>
      ) : null}

      {phase === "processing" ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress color="success" />
          <Typography fontWeight={600}>{statusMessage || "Processing…"}</Typography>
          <Box sx={{ width: "100%", maxWidth: 360 }}>
            <LinearProgress color="success" />
          </Box>
        </Stack>
      ) : null}

      {phase === "results" ? (
        <Stack spacing={2}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "#ECFDF5",
              border: "1px solid #A7F3D0",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" fontWeight={800} color="#047857">
              {hasPct ? `${Math.round(pct)}%` : "—"}
            </Typography>
            <Typography color="text.secondary">{summary?.performance_level || "Reviewed"}</Typography>
          </Box>

          {Array.isArray(summary?.strengths) && summary.strengths.length > 0 ? (
            <Box>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                Strengths
              </Typography>
              {summary.strengths.map((s, i) => (
                <Typography key={i} variant="body2">
                  • {s}
                </Typography>
              ))}
            </Box>
          ) : null}
          {Array.isArray(summary?.recommendations) && summary.recommendations.length > 0 ? (
            <Box>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                Next steps
              </Typography>
              {summary.recommendations.map((s, i) => (
                <Typography key={i} variant="body2">
                  • {s}
                </Typography>
              ))}
            </Box>
          ) : null}

          {pieces.map((p, idx) => (
            <Accordion
              key={idx}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid #E2E8F0", borderRadius: 2, "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={700}>
                  {p.title || `Piece ${p.number ?? idx + 1}`}
                  {p.scores?.overall != null ? ` · ${Math.round(p.scores.overall)}%` : ""}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {p.feedback ? <Typography variant="body2">{p.feedback}</Typography> : null}
                  {p.transcription ? (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                      {p.transcription}
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          <Button variant="outlined" onClick={reset} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
            Review another piece
          </Button>
        </Stack>
      ) : null}
    </WizardPageShell>
  );
}
